import { api } from '@packages/backend/convex/_generated/api';
import { zCommandStatus } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { LogJson } from '@/components/LogJson';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RemoteCommandStatusBadge } from './RemoteCommandStatusBadge';

export const Route = createFileRoute('/a/remote-commands/$commandId')({
  validateSearch: (search: Record<string, unknown>) => ({
    status: zCommandStatus.default('PENDING').parse(search.status),
  }),
  component: RemoteCommandDetailsPage,
});

function RemoteCommandDetailsPage() {
  const { commandId } = Route.useParams();
  const { status } = Route.useSearch();

  const {
    data: command,
    error,
    isError,
    isPending,
  } = useQuery(api.remoteCommands.getById, {
    commandId,
  });

  if (isPending) {
    return <LoadingIcon className="mt-8" />;
  }

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading command" />;
  }

  if (!command) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Remote Command Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-gray-500">Command not found</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Link
          to="/a/remote-commands"
          search={{ status }}
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to list
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardTitle>Remote Command Details</CardTitle>
            <RemoteCommandStatusBadge status={command.commandStatus} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Command ID</h3>
              <p className="text-sm font-mono">{command._id}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Created</h3>
              <p className="text-sm">
                {formatDistanceToNow(command._creationTime, { addSuffix: true })}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Command Type</h3>
              <p className="text-sm font-mono">{command.commandType}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Target Terminal</h3>
              <p className="text-sm font-mono">{command.targetPosTerminalUuid}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Restaurant ID</h3>
              <p className="text-sm font-mono">{command.restaurantId}</p>
            </div>
          </div>

          {command.commandPayload && Object.keys(command.commandPayload).length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Command Payload</h3>
              <LogJson jsonData={command.commandPayload} requireDebugParam={false} />
            </div>
          )}

          {command.commandResult && Object.keys(command.commandResult).length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Command Result</h3>
              <LogJson jsonData={command.commandResult} requireDebugParam={false} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
